import { useState } from "react";
import { css, cx } from "styled-system/css";
import { X } from "lucide-react";
import { cf } from "@/components/brand/sfturnos-styles";
import {
  AMENITIES_CATALOG,
  createCustomAmenity,
  getAmenityIcon,
  predefinedAmenityToItem,
  type AmenityItem,
} from "@/lib/amenities-catalog";

type Props = {
  value: AmenityItem[];
  onChange: (next: AmenityItem[]) => void;
};

export function SettingsAmenitiesBlock({ value, onChange }: Props) {
  const [customDraft, setCustomDraft] = useState("");

  function togglePredefined(id: string, checked: boolean) {
    if (checked) {
      const entry = AMENITIES_CATALOG.find((a) => a.id === id);
      if (!entry) return;
      onChange([...value, predefinedAmenityToItem(entry)]);
    } else {
      onChange(value.filter((a) => !(a.id === id && a.tipo === "predefinido")));
    }
  }

  function addCustom() {
    const nombre = customDraft.trim();
    if (!nombre) return;
    const alreadyExists = value.some((a) => a.nombre.trim().toLowerCase() === nombre.toLowerCase());
    if (alreadyExists) {
      setCustomDraft("");
      return;
    }
    onChange([...value, createCustomAmenity(nombre)]);
    setCustomDraft("");
  }

  function removeCustom(id: string) {
    onChange(value.filter((a) => a.id !== id));
  }

  const customItems = value.filter((a) => a.tipo === "custom");

  return (
    <div className={cx(cf.dashSurface, css({ p: "5" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "2" })}>Amenities</p>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "4", lineHeight: "1.5" })}>
        Comodidades de tu local. Se muestran en la pestaña "Info" de tu agenda online.
      </p>

      <div
        className={css({
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
          gap: "2",
          mb: "4",
        })}
      >
        {AMENITIES_CATALOG.map((entry) => {
          const Icon = getAmenityIcon(entry.icono);
          const checked = value.some((a) => a.id === entry.id && a.tipo === "predefinido");
          return (
            <label
              key={entry.id}
              className={cx(
                css({
                  display: "flex",
                  alignItems: "center",
                  gap: "2",
                  fontSize: "sm",
                  color: "#334155",
                  px: "3",
                  py: "2",
                  rounded: "md",
                  borderWidth: "1px",
                  cursor: "pointer",
                }),
                checked
                  ? css({ borderColor: "token(colors.brand.400)", bg: "token(colors.brand.50)" })
                  : css({ borderColor: "#e2e8f0" }),
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => togglePredefined(entry.id, e.target.checked)}
                className={css({ flexShrink: 0 })}
              />
              <Icon size={16} className={css({ flexShrink: 0, color: "#64748b" })} />
              <span>{entry.nombre}</span>
            </label>
          );
        })}
      </div>

      {customItems.length > 0 ? (
        <div className={css({ display: "flex", flexWrap: "wrap", gap: "2", mb: "3" })}>
          {customItems.map((item) => (
            <span
              key={item.id}
              className={css({
                display: "inline-flex",
                alignItems: "center",
                gap: "2",
                fontSize: "sm",
                color: "#334155",
                px: "3",
                py: "1.5",
                rounded: "full",
                bg: "#f1f5f9",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
              })}
            >
              {item.nombre}
              <button
                type="button"
                onClick={() => removeCustom(item.id)}
                aria-label={`Quitar ${item.nombre}`}
                className={css({
                  display: "inline-flex",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  p: "0",
                  _hover: { color: "#64748b" },
                })}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className={css({ display: "flex", gap: "2", maxW: "24rem" })}>
        <input
          className={cf.input}
          value={customDraft}
          onChange={(e) => setCustomDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Otra amenity (ej: Cancha techada)"
          maxLength={80}
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customDraft.trim()}
          className={css({
            px: "4",
            py: "2",
            rounded: "md",
            fontSize: "sm",
            fontWeight: "medium",
            borderWidth: "1px",
            borderColor: "#cbd5e1",
            bg: "#fff",
            cursor: "pointer",
            whiteSpace: "nowrap",
            _disabled: { opacity: 0.5, cursor: "not-allowed" },
          })}
        >
          Agregar
        </button>
      </div>
    </div>
  );
}
